import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { base_url } from "../api/index";
import { POS_ENABLED } from "../api/pos/config";
import { getPosWs } from "../api/pos/posWs";

const PermissionsContext = createContext<any>({
  permissions: [],
  role: "",
  loading: true,
  hasPermission: () => false,
  canAccessRoute: () => false,
  getDefaultRoute: () => "/",
  refreshPermissions: () => { },
});

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const MANAGER_ROLES = new Set(["manager", "menecer", "admin", "owner", "super-admin", "superadmin"]);

// Test üçün ixrac olunur (bax: PermissionsContext.test.ts).
export const ROLE_ROUTE_ALLOWLIST = {
  waiter: ["/masalar", "/masa-siparis", "/couriers", "/genel-ayarlar"],
  // Aşpaz: əsas iş yeri mətbəx ekranıdır; masaya nə sifariş verildiyini də görür.
  chef: ["/kitchen", "/masalar", "/masa-siparis", "/genel-ayarlar"],
  "kitchen-manager": ["/kitchen", "/printers", "/masalar", "/masa-siparis", "/material", "/stok", "/genel-ayarlar"],
  courier: ["/siparisler", "/couriers"],
  // Kassir: masa/sifariş + ödəniş/günlük kassa (əsas iş yeri /gunluk-kasa).
  cashier: [
    "/masalar",
    "/masa-siparis",
    "/gunluk-kasa",
    "/siparisler",
    "/musteriler",
    "/muster-siparis-ekle",
    "/couriers",
  ],
};

const STAFF_SAFE_FALLBACK_ALLOWLIST = ["/masalar", "/masa-siparis", "/couriers", "/genel-ayarlar"];

// Route → tələb olunan icazələr (HƏR HANSI biri kifayətdir). Umbrella (legacy)
// icazələrlə yanaşı granular Spatie icazələri də sadalanır ki, rezmo_admin-də
// yalnız granular icazələrlə yaradılmış custom rollar da işləsin.
// QEYD: "/genel-ayarlar" (bildirişlər səhifəsi) qəsdən xəritədə YOXDUR —
// istənilən daxil olmuş istifadəçiyə açıqdır.
// Test üçün ixrac olunur (bax: PermissionsContext.test.ts) — marşrutun hansı
// icazələri qəbul etdiyi təhlükəsizlik qərarıdır və qorunmalıdır.
export const ROUTE_PERMISSION_MAP: Record<string, string[]> = {
  "/masalar": ["manage-tables", "table-order", "tables.view"],
  "/masa-siparis": ["manage-tables", "table-order", "table-orders.view"],
  "/siparisler": ["manage-quick-orders", "quick-orders.view"],
  "/musteriler": ["manage-customers", "customers.view"],
  "/muster-siparis-ekle": ["manage-customers", "customers.view"],
  "/gunluk-kasa": ["access-payments", "manage-payments", "payments.view-daily", "payments.process"],
  "/couriers": ["manage-quick-orders", "manage-couriers", "couriers.view"],
  "/stok": ["manage-tanimlar", "manage-stocks", "stocks.view"],
  "/masa-tanimlari": ["manage-tanimlar", "manage-table-groups", "tables.create", "table-groups.manage"],
  "/personel-tanimlari": ["manage-staff", "manage-users", "staff.view", "staff.manage"],
  "/material": ["manage-tanimlar", "raw-materials.view", "raw-materials.manage"],
  "/expenses": ["access-payments", "expenses.view", "expenses.manage"],
  "/stocksadd": ["manage-tanimlar", "manage-stocks", "stocks.create"],
  // Mətbəx ekranı: əvvəl xəritədə YOX idi, ona görə hər kəsə açıq qalırdı.
  //
  // `manage-tanimlar` QƏSDƏN ÇIXARILIB: o, anbar/tərif umbrella-sıdır
  // (stoklar, masa qrupları, reseptlər) və mətbəx ekranı ilə əlaqəsi yoxdur.
  "/kitchen": ["kitchen.display", "kitchen.manage", "manage-kitchen"],

  // ── PRINTERLƏR: UMBRELLA QƏBUL ETMİR ──
  //
  // Əvvəl siyahıda `manage-kitchen` də vardı. O isə paneldə YALNIZ
  // `kitchen.display` işarələnəndə avtomatik törəyir (bax: rezmo_admin
  // RolePermissionBridge::legacyMapping). Nəticədə "mətbəx ekranına bax"
  // icazəsi verilmiş aşpaz POS-da PRİNTERLƏRİ də görürdü — admin bunu heç
  // yerdə seçməmişdi və səbəbi ekranda görünmürdü.
  //
  // İndi yalnız birbaşa printer icazələri açır.
  "/printers": ["kitchen.manage", "printers.view", "printers.manage"],
};

const DEFAULT_PRIVATE_ROUTES = ["/masalar", "/siparisler", "/couriers", "/musteriler", "/gunluk-kasa", "/genel-ayarlar"];

const extractNames = (arr) => {
  if (!arr || !Array.isArray(arr)) return [];
  return arr.map((p) => (typeof p === "string" ? p : p?.name)).filter(Boolean);
};

const normalizeRole = (value) => (typeof value === "string" ? value.trim().toLowerCase() : "");

// İcazə adlarını formatdan asılı olmayan şəklə gətir: "Access Payments",
// "access_payments", "access-payments" → "access-payments". Spatie-də fərqli
// yazılış olsa belə front-un ROUTE_PERMISSION_MAP-ı ilə uyğun gəlsin deyə.
const normalizePermission = (value) =>
  typeof value === "string" ? value.trim().toLowerCase().replace(/[\s_]+/g, "-") : "";

const normalizeString = (value) => (typeof value === "string" ? value.trim().toLowerCase() : "");

const tokenizeName = (value) =>
  normalizeString(value)
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

const getInitials = (value) => tokenizeName(value).map((part) => part[0]).join("");

const namesLikelySame = (first, second) => {
  const a = normalizeString(first);
  const b = normalizeString(second);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;

  const aTokens = tokenizeName(a);
  const bTokens = tokenizeName(b);

  if (aTokens.length > 0 && bTokens.length > 0 && aTokens[0] === bTokens[0]) {
    return true;
  }

  const aInitials = getInitials(a);
  const bInitials = getInitials(b);
  if (!aInitials || !bInitials) return false;

  return (
    aInitials === bInitials ||
    aInitials.startsWith(bInitials) ||
    bInitials.startsWith(aInitials)
  );
};

const safeParseArray = (raw) => {
  if (!raw || typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Rəhbər sözləri — rol adında bunlardan biri varsa rol "rəhbər"dir. Amma NƏYİN
// rəhbəri olduğu ayrıca müəyyən edilir (aşağıya bax).
// QEYD: "şef" QƏSDƏN yoxdur. Türkcədə "şef" aşpaz başçısı deməkdir, yəni
// mətbəx rolu — onu rəhbər saysaq restoranın hər yerinə giriş verərdik.
// Tanınmayan rol "" qaytarır və giriş icazələrlə həll olunur; bu, səhv
// tanımaqdan təhlükəsizdir.
const LEAD_HINTS = ["manager", "menecer", "müdir", "mudir", "müdür", "mudur", "rəhbər", "rehber", "admin", "owner"];

// Bölmə sözləri — rol yalnız həmin bölməyə aiddir.
//
// NİYƏ LAZIMDIR: "kitchen-manager" sətri "manager" ehtiva edir. Aşağıdakı
// ümumi yoxlama sadəcə `includes("manager")` etdiyi üçün mətbəx rəhbərini
// RESTORAN meneceri kimi tanıyır və ona bütün POS-a tam giriş verirdi —
// istifadəçi Müştərilər, Kassa, Sifarişlər bölmələrini görürdü. Eyni tələ
// "bar meneceri", "anbar müdiri" kimi custom rollar üçün də qurulub.
const KITCHEN_HINTS = ["kitchen", "mətbəx", "metbex", "mutfak", "кухн", "chef", "aşpaz", "aspaz", "aşçı", "asci", "повар"];
const OTHER_DEPARTMENT_HINTS = ["bar", "anbar", "stok", "depo", "warehouse", "склад"];

const containsAny = (value, hints) => hints.some((hint) => value.includes(hint));

// Test üçün ixrac olunur — bu funksiya rol adından səlahiyyət səviyyəsi çıxarır
// və səhvi bütün POS-a tam giriş deməkdir.
export const resolveRolePolicyKey = (roleValue) => {
  const normalized = normalizeRole(roleValue);
  if (!normalized) return "";

  // 1) Dəqiq restoran-səviyyəli rollar. Yalnız TAM uyğunluq — heç bir substring.
  if (MANAGER_ROLES.has(normalized)) return "manager";

  const isLead = containsAny(normalized, LEAD_HINTS);

  // 2) Mətbəx ailəsi. Ümumi menecer yoxlamasından ƏVVƏL gəlməlidir.
  if (containsAny(normalized, KITCHEN_HINTS)) {
    return isLead ? "kitchen-manager" : "chef";
  }

  // 3) Başqa bölmə rəhbərləri ("bar meneceri", "anbar müdiri"). Bunlar üçün
  //    hazır allowlist yoxdur, ona görə "" qaytarırıq — giriş tamamilə Spatie
  //    icazələri ilə həll olunur. Bu, boş allowlist-dən daha doğrudur: rol adı
  //    deyil, real icazə qərar verir.
  if (isLead && containsAny(normalized, OTHER_DEPARTMENT_HINTS)) return "";

  if (
    normalized.includes("waiter") ||
    normalized.includes("garson") ||
    normalized.includes("servis")
  ) {
    return "waiter";
  }

  if (normalized.includes("courier") || normalized.includes("kurye") || normalized.includes("kuryer")) {
    return "courier";
  }

  if (
    normalized.includes("cashier") ||
    normalized.includes("kassir") ||
    normalized.includes("kasiyer") ||
    normalized.includes("kassa")
  ) {
    return "cashier";
  }

  // 4) Bölmə adı olmayan ümumi rəhbər rolları ("baş menecer", "administrator").
  if (isLead) return "manager";

  return "";
};

const extractRoleFromMe = (userData) => {
  if (!userData || typeof userData !== "object") return "";

  if (Array.isArray(userData.roles) && userData.roles.length > 0) {
    const roleEntry = userData.roles.find(Boolean);
    if (typeof roleEntry === "string" && roleEntry.trim()) return roleEntry;
    if (roleEntry?.name && typeof roleEntry.name === "string") return roleEntry.name;
  }

  const fallbackCandidates = [
    userData.role,
    userData.staff_role,
    userData.user_role,
    userData?.staff?.role,
    userData?.user?.role,
    userData?.data?.role,
  ];

  const found = fallbackCandidates.find((value) => typeof value === "string" && value.trim());
  return found || "";
};

const matchesRoute = (path, prefix) => path === prefix || path.startsWith(prefix + "/");

const isPublicPath = (path) => path === "/" || path.startsWith("/adminPage") || path.startsWith("/order-details");

export const resolveRequiredPermissions = (path): string[] | null => {
  for (const [prefix, permissions] of Object.entries(ROUTE_PERMISSION_MAP)) {
    if (matchesRoute(path, prefix)) {
      return permissions;
    }
  }
  return null;
};

const canRoleAccessPath = (path, role) => {
  const policyKey = resolveRolePolicyKey(role);
  if (!policyKey) return null;
  if (policyKey === "manager") return true;

  const allowlist = ROLE_ROUTE_ALLOWLIST[policyKey];
  if (!allowlist) return null;

  return allowlist.some((prefix) => matchesRoute(path, prefix));
};

// Son məlum icazələr (staff-login cavabındakı all_permissions Login.tsx-də yazılır).
// Offline POS girişində /me əlçatmaz olur — bu seed olmadan staff heç nə görməzdi.
const readStoredPermissions = (): string[] =>
  safeParseArray(localStorage.getItem("staff_permissions"))
    .map(normalizePermission)
    .filter(Boolean);

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState<any[]>(readStoredPermissions);
  const [role, setRole] = useState(() => localStorage.getItem("staff_role") || localStorage.getItem("role") || "");
  const [loading, setLoading] = useState(!!localStorage.getItem("token"));

  const rolePolicyKey = useMemo(() => resolveRolePolicyKey(role), [role]);
  const isManager = useMemo(() => rolePolicyKey === "manager", [rolePolicyKey]);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setPermissions([]);
      setRole("");
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(`${base_url}/me`, getAuthHeaders());
      const userData = response.data;

      const apiRole = extractRoleFromMe(userData);
      let storedStaffRole = localStorage.getItem("staff_role") || "";
      const storedRole = localStorage.getItem("role") || "";

      if (!storedStaffRole) {
        const cachedStaffList = safeParseArray(localStorage.getItem("staff_list"));
        if (cachedStaffList.length > 0) {
          const meId = userData?.id;
          const meName = normalizeString(userData?.name);
          const meUserName = normalizeString(userData?.username);
          const meEmail = normalizeString(userData?.email);

          const matchedStaff = cachedStaffList.find((staff) => {
            if (!staff || typeof staff !== "object") return false;

            if (meId && staff.id && String(staff.id) === String(meId)) return true;

            const staffCandidateNames = [
              staff.name,
              staff.full_name,
              staff.username,
              staff.email,
            ].filter(Boolean);

            const meCandidateNames = [meName, meUserName, meEmail].filter(Boolean);

            return staffCandidateNames.some((staffValue) =>
              meCandidateNames.some((meValue) => namesLikelySame(meValue, staffValue))
            );
          });

          if (matchedStaff?.role) {
            storedStaffRole = normalizeRole(matchedStaff.role);
            localStorage.setItem("staff_role", storedStaffRole);
            localStorage.setItem("role", storedStaffRole);
          }
        }
      }

      let userRole = apiRole || storedStaffRole || storedRole || "";

      // Staff selector role is the most reliable source for waiter/courier sessions.
      if (storedStaffRole) {
        userRole = storedStaffRole;
      } else {
        const storedPolicy = resolveRolePolicyKey(storedRole);
        if (storedPolicy && storedPolicy !== "manager") {
          userRole = storedRole;
        }
      }

      setRole(userRole);
      if (userRole) {
        localStorage.setItem("role", userRole);
      }

      if (storedStaffRole) {
        localStorage.setItem("staff_role", storedStaffRole);
      }

      const mergedPermissions = new Set();

      // Əsas mənbə: backend /me-nin effektiv icazə siyahısı (birbaşa + rollar
      // vasitəsilə). rezmo_admin-də yaradılan custom rollar üçün yeganə etibarlı
      // mənbə budur — rol adı deyil.
      extractNames(userData.all_permissions).forEach((permission) => mergedPermissions.add(normalizePermission(permission)));

      extractNames(userData.permissions).forEach((permission) => mergedPermissions.add(normalizePermission(permission)));
      extractNames(userData.assigned_permissions).forEach((permission) => mergedPermissions.add(normalizePermission(permission)));
      extractNames(userData.role_permissions).forEach((permission) => mergedPermissions.add(normalizePermission(permission)));

      if (Array.isArray(userData.roles)) {
        userData.roles.forEach((currentRole) => {
          if (currentRole?.permissions) {
            extractNames(currentRole.permissions).forEach((permission) => mergedPermissions.add(normalizePermission(permission)));
          }
        });
      }

      let permissionList = [...mergedPermissions];
      if (permissionList.length > 0) {
        // Offline girişlər üçün persist et (Login.tsx yalnız staff-login anında yazır;
        // /me daha tam ola bilər — məs. admin/owner sessiyaları).
        localStorage.setItem("staff_permissions", JSON.stringify(permissionList));
      } else {
        // Köhnə backend /me-də all_permissions yoxdur — staff-login zamanı yazılmış
        // son məlum icazələri boş cavabla əzmə (miqrasiya pəncərəsi + geriyə uyğunluq).
        permissionList = readStoredPermissions();
      }
      setPermissions(permissionList);
    } catch (error: any) {
      console.error("[Permissions] Error fetching /me:", error);
      // Offline / şəbəkə xətası: son məlum icazələri SIFIRLA-MA — POS offline
      // rejimdə staff yenə öz səhifələrini görməlidir.
      setPermissions(readStoredPermissions());
      setRole(localStorage.getItem("staff_role") || localStorage.getItem("role") || "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // ── Rol dəyişikliyi POS-a DƏRHAL çatsın ───────────────────────────────────
  //
  // Əvvəl icazələr yalnız giriş anında oxunurdu. Admin paneldə menecerin rolu
  // dəyişsə də POS-da işçi növbə bitənə qədər köhnə səlahiyyətlə işləməyə
  // davam edirdi — həm səhv, həm də təhlükəsizlik problemi (səlahiyyəti
  // ALINAN adam onu saxlayırdı).
  //
  // Zəncir: admin panel → `users.cloud_revision` artır → bulud invalidation
  // (və ya ~10 saniyəlik yüngül yoxlama) → pos_local `staff` stream-i çəkir →
  // yerli WS `staff.*` yayımlayır → bu effekt icazələri yenidən oxuyur.
  useEffect(() => {
    if (!POS_ENABLED) return undefined;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = getPosWs().onEvent((envelope) => {
      if (!String(envelope?.type || '').startsWith('staff.')) return;
      // Bir sinxron keçidində bir neçə işçi yenilənə bilər — hamısı üçün
      // ayrıca `/me` sorğusu atmaq mənasızdır.
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void fetchPermissions();
      }, 500);
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [fetchPermissions]);

  // Tək icazə (string) və ya siyahı (HƏR HANSI biri kifayətdir) qəbul edir.
  const hasPermission = useCallback(
    (permissionName) => {
      if (isManager) return true;
      if (!permissionName) return true;
      if (Array.isArray(permissionName)) {
        return permissionName.some((p) => permissions.includes(normalizePermission(p)));
      }
      return permissions.includes(normalizePermission(permissionName));
    },
    [permissions, isManager]
  );

  // İcazə-əsaslı (Spatie-uyğun) giriş yoxlaması.
  //
  // Prinsip: əsas mənbə İCAZƏDİR (permission), rol adı yox. Admin paneldə Spatie ilə
  // YENİ rol yaradılıb ona icazələr verildikdə, front avtomatik işləyir — şərt odur ki,
  // icazə adları `ROUTE_PERMISSION_MAP`-dakı adlarla uyğun gəlsin (məs. "access-payments").
  // Statik rol allowlist-i yalnız KEÇİD/fallback üçün qalır (backend hələ icazə qaytarmırsa
  // və ya köhnə rollar üçün).
  const canAccessRoute = useCallback(
    (path) => {
      if (!path) return false;
      if (isPublicPath(path)) return true;
      if (!localStorage.getItem("token")) return false;
      if (isManager) return true; // manager/admin/owner → tam giriş

      const requiredPermissions = resolveRequiredPermissions(path);

      // 1) Spatie icazəsi (ƏSAS): route-un tələb etdiyi icazələrdən HƏR HANSI biri
      //    istifadəçidə varsa → icazə ver. Rol adından asılı deyil (custom rollar üçün).
      if (requiredPermissions && permissions.length > 0) {
        return hasPermission(requiredPermissions);
      }

      // 2) Statik rol allowlist (icazələr backend-dən hələ gəlməyibsə — köhnə
      //    backend / ilk giriş / miqrasiya dövrü üçün fallback).
      if (canRoleAccessPath(path, role) === true) return true;

      // 3) Staff sessiyası + naməlum rol → təhlükəsiz minimal fallback.
      if (localStorage.getItem("device_token")) {
        return STAFF_SAFE_FALLBACK_ALLOWLIST.some((prefix) => matchesRoute(path, prefix));
      }

      // 4) Route-a icazə xəritələnməyibsə aç; əks halda son icazə yoxlaması.
      if (!requiredPermissions) return true;
      return hasPermission(requiredPermissions);
    },
    [role, hasPermission, isManager, permissions]
  );

  const getDefaultRoute = useCallback(() => {
    if (!localStorage.getItem("token")) return "/";

    const isStaffSession = !!localStorage.getItem("device_token");

    const roleRoutes = rolePolicyKey ? ROLE_ROUTE_ALLOWLIST[rolePolicyKey] : null;
    if (roleRoutes?.length) {
      const matchedRoleRoute = roleRoutes.find((route) => canAccessRoute(route));
      if (matchedRoleRoute) return matchedRoleRoute;
    }

    if (isStaffSession) {
      const safeRoute = STAFF_SAFE_FALLBACK_ALLOWLIST.find((route) => canAccessRoute(route));
      if (safeRoute) return safeRoute;
      // Custom rol yalnız başqa bölmələrə icazəlidirsə (məs. yalnız müştərilər),
      // əsas route-lar arasından icazəlisini tap.
      const permittedRoute = DEFAULT_PRIVATE_ROUTES.find((route) => canAccessRoute(route));
      if (permittedRoute) return permittedRoute;
      return "/";
    }

    for (const route of DEFAULT_PRIVATE_ROUTES) {
      if (canAccessRoute(route)) return route;
    }

    return "/";
  }, [rolePolicyKey, canAccessRoute]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        role,
        loading,
        hasPermission,
        canAccessRoute,
        getDefaultRoute,
        refreshPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);

export default PermissionsContext;


